import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import type { ProjectBrainDTO, ProjectDetailDTO } from "@app-factory/shared";
import { api } from "@/lib/api";
import { cs } from "@/i18n/cs";
import { Btn, Chip, EmptyState, Panel, inputClass } from "@/design/primitives";
import { relativeTime } from "@/lib/format";
import { canContribute, useProjectRole } from "../projects/project-data";
import { BrainGraph } from "./BrainGraph";

/**
 * Brain projektu — „co o tomhle projektu víme".
 *
 * Obě půlky pohromadě, protože otázka je jedna: mapa kódu (kde co je) a
 * poučení platná jen tady. Ve Factory leží každá v jiné tabulce, ale to je
 * detail uložení, ne to, na co se člověk ptá.
 *
 * Ukazuje i to, co worker NEDOSTANE — položky bez spouštěče. Právě ty čekají
 * na doplnění a jinde by je nikdo neuviděl.
 */
export function ProjectBrainTab({ project }: { project: ProjectDetailDTO }) {
  const projectId = project.id;
  const contributor = canContribute(useProjectRole(project));
  const [selected, setSelected] = useState<string | null>(null);

  const brain = useQuery({
    queryKey: ["projects", projectId, "brain"],
    queryFn: () => api.projectBrain(projectId),
  });
  const graph = useQuery({
    queryKey: ["brain", "graph", "project", projectId],
    queryFn: () => api.brainGraph({ projectId }),
  });

  const data = brain.data;
  const drafts = useMemo(
    () =>
      data ? data.maps.filter((m) => !m.indexed).length + data.lessons.filter((l) => !l.indexed).length : 0,
    [data],
  );

  if (!data) return <Panel className="p-4">{cs.projectBrain.loading}</Panel>;

  return (
    <div className="flex flex-col gap-3">
      <Panel className="p-3">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="af-label" style={{ color: "var(--accent)" }}>
            {cs.projectBrain.title}
          </span>
          <span className="text-[12px]" style={{ color: "var(--text-tertiary)" }}>
            {cs.projectBrain.summary(data.maps.length, data.lessons.length, data.unmapped.length)}
          </span>
          {drafts > 0 ? (
            <span className="ml-auto text-[12px]" style={{ color: "var(--warning)" }}>
              {cs.projectBrain.drafts(drafts)}
            </span>
          ) : null}
        </div>
        <p className="mt-1.5 text-[12px] leading-relaxed" style={{ color: "var(--text-secondary)" }}>
          {cs.projectBrain.note}
        </p>
        {/* Vault má strop a řeže podle abecedy. Bez tohohle řádku část brainu
            mizí beze stopy: v UI je vidět, u workera už ne. */}
        {data.vault.omitted > 0 ? (
          <p
            className="mt-1.5 text-[12px] leading-relaxed"
            style={{ color: "var(--warning)" }}
            data-testid="brain-vault-truncated"
          >
            {cs.projectBrain.vaultTruncated(data.vault.omitted, data.vault.eligible, data.vault.limit)}
          </p>
        ) : null}
      </Panel>

      <div className="grid gap-3 xl:grid-cols-[1.15fr_1fr]">
        <div className="flex flex-col gap-3">
          <MapSection
            maps={data.maps}
            unmapped={data.unmapped}
            projectId={projectId}
            contributor={contributor}
          />
          <LessonSection lessons={data.lessons} />
        </div>

        {/* výška je určená, ne odvozená z obsahu — plátno grafu jinak nemá
            z čeho počítat svých 100 % */}
        <Panel
          className="flex flex-col overflow-hidden p-0"
          style={{ height: "clamp(380px, calc(100vh - 340px), 760px)" }}
        >
          <div
            className="px-3 py-2 text-[11px]"
            style={{ color: "var(--text-tertiary)", borderBottom: "1px solid var(--border-default)" }}
          >
            {cs.projectBrain.graphNote}
          </div>
          <div className="min-h-0 flex-1">
            {graph.data && graph.data.nodes.length > 0 ? (
              <BrainGraph graph={graph.data} selected={selected} onSelect={setSelected} />
            ) : (
              <div className="flex h-full items-center justify-center p-6">
                <EmptyState title={cs.projectBrain.graphEmpty} />
              </div>
            )}
          </div>
        </Panel>
      </div>
    </div>
  );
}

/**
 * Jedna mapa. Klikací: mapa, kterou si nejde otevřít, je jen řádek s metadaty —
 * a člověk se potřebuje podívat, co v ní vlastně stojí. Obsah se dotahuje až
 * při rozbalení, ať se s každou návštěvou záložky netahá pár kilobajtů na mapu.
 */
function MapRow({
  map: m,
  projectId,
  contributor,
}: {
  map: ProjectBrainDTO["maps"][number];
  projectId: string;
  contributor: boolean;
}) {
  const [open, setOpen] = useState(false);
  const doc = useQuery({
    queryKey: ["knowledge", m.id],
    queryFn: () => api.knowledgeDoc(m.id),
    enabled: open,
  });

  return (
    <div style={{ borderRadius: "var(--radius-box)", border: "1px solid var(--border-default)" }}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full px-2 py-1.5 text-left"
        data-testid={`brain-map-${m.codebaseSlug ?? m.slug}`}
      >
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="text-[11px]" style={{ color: "var(--accent)" }}>
            {open ? "▾" : "▸"}
          </span>
          <span className="text-[11px]" style={{ color: "var(--text-primary)" }}>
            {m.title}
          </span>
          {m.codebaseSlug ? <Chip tone="cyan">{m.codebaseSlug}</Chip> : null}
          {!m.indexed ? <Chip tone="amber">{cs.projectBrain.noTrigger}</Chip> : null}
        </div>
        <div className="mt-0.5 pl-4 text-[11px]" style={{ color: "var(--text-tertiary)" }}>
          {m.codeCommit ? (
            <span>
              {cs.projectBrain.verifiedAt}{" "}
              <span className="font-mono" style={{ color: "var(--text-secondary)" }}>
                {/* plné SHA se čte mizerně a stejně se porovnává s `git log --oneline` */}
                {m.codeCommit.slice(0, 7)}
              </span>
              {m.codeVerifiedAt ? ` · ${relativeTime(m.codeVerifiedAt)}` : ""}
            </span>
          ) : (
            // bez commitu nejde poznat, jestli mapa ještě platí
            <span style={{ color: "var(--warning)" }}>{cs.projectBrain.noCommit}</span>
          )}
          {" · "}
          {cs.projectBrain.chars(m.chars)}
          {" · "}
          <span style={{ color: "var(--accent)" }}>
            {open ? cs.projectBrain.collapse : cs.projectBrain.expand}
          </span>
        </div>
      </button>

      {/* Spouštěč je mimo tlačítko schválně: bez něj mapa do vaultu workera
          vůbec nejde, a doplnit se dal dosud jen z běžící session workera. */}
      <TriggerRow docId={m.id} projectId={projectId} readWhen={m.readWhen} contributor={contributor} />

      {open ? (
        <div
          className="mx-2 mb-2 max-h-[420px] overflow-auto whitespace-pre-wrap px-3 py-2 font-mono text-[12px] leading-[1.7]"
          style={{
            borderTop: "1px solid var(--border-default)",
            color: "var(--text-secondary)",
            background: "var(--surface-inset)",
          }}
          data-testid={`brain-map-content-${m.codebaseSlug ?? m.slug}`}
        >
          {doc.data ? doc.data.doc.content : cs.projectBrain.loading}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Spouštěč dokumentu — jediné pole, které rozhoduje, jestli se dokument
 * workerovi vůbec nabídne. Ukládá se samostatně, bez revize: nemění se pravda,
 * jen způsob nabízení (stejně to dělá i worker endpoint).
 */
function TriggerRow({
  docId,
  projectId,
  readWhen,
  contributor,
}: {
  docId: string;
  projectId: string;
  readWhen: string;
  contributor: boolean;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(readWhen);
  const [error, setError] = useState<string | null>(null);
  const indexed = readWhen.trim() !== "";

  const save = useMutation({
    mutationFn: () => api.setKnowledgeReadWhen(docId, draft),
    onSuccess: () => {
      setEditing(false);
      void queryClient.invalidateQueries({ queryKey: ["projects", projectId, "brain"] });
      void queryClient.invalidateQueries({ queryKey: ["brain", "graph", "project", projectId] });
    },
    onError: (err) => setError(err instanceof Error ? err.message : cs.common.error),
  });

  if (editing) {
    return (
      <div className="flex items-center gap-2 px-2 pb-1.5 pl-6">
        <input
          className={`${inputClass} text-[11px]`}
          value={draft}
          autoFocus
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") save.mutate();
            if (e.key === "Escape") setEditing(false);
          }}
          placeholder={cs.projectBrain.mapTriggerPlaceholder}
          data-testid={`brain-map-trigger-input-${docId}`}
        />
        <Btn variant="primary" size="sm" disabled={save.isPending} onClick={() => save.mutate()}>
          {cs.projectBrain.mapTriggerSave}
        </Btn>
        <Btn size="sm" onClick={() => setEditing(false)}>
          {cs.common.cancel}
        </Btn>
        {error ? (
          <span className="text-[11px]" style={{ color: "var(--critical)" }}>
            ⚠ {error}
          </span>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-baseline gap-2 px-2 pb-1.5 pl-6 text-[11px]">
      <span style={{ color: indexed ? "var(--text-tertiary)" : "var(--warning)" }}>
        {indexed ? readWhen : cs.projectBrain.noTriggerNote}
      </span>
      {contributor ? (
        <button
          type="button"
          onClick={() => {
            setDraft(readWhen);
            setError(null);
            setEditing(true);
          }}
          style={{ color: "var(--accent)" }}
          data-testid={`brain-map-trigger-edit-${docId}`}
        >
          {indexed ? cs.projectBrain.mapTriggerChange : cs.projectBrain.mapTriggerEdit}
        </button>
      ) : null}
    </div>
  );
}

/**
 * Mapa kódu. Klíčový sloupec je stáří: mapa ověřená na starém commitu je
 * vodítko, ne jistota — a mapa, která lže tiše, je horší než žádná.
 */
function MapSection({
  maps,
  unmapped,
  projectId,
  contributor,
}: {
  maps: ProjectBrainDTO["maps"];
  unmapped: ProjectBrainDTO["unmapped"];
  projectId: string;
  contributor: boolean;
}) {
  return (
    <Panel className="p-3">
      <div className="af-label mb-2" style={{ color: "var(--accent)" }}>
        {cs.projectBrain.mapTitle}
      </div>

      {maps.length === 0 && unmapped.length === 0 ? (
        <EmptyState title={cs.projectBrain.noCodebases} />
      ) : null}

      <div className="flex flex-col gap-1.5">
        {maps.map((m) => (
          <MapRow key={m.id} map={m} projectId={projectId} contributor={contributor} />
        ))}

        {unmapped.map((c) => (
          <div
            key={c.slug}
            className="px-2 py-1.5"
            style={{ border: "1px dashed var(--border-default)", opacity: 0.75 }}
          >
            <span className="text-[11px]" style={{ color: "var(--text-secondary)" }}>
              {c.name}
            </span>
            <span className="ml-2 text-[11px]" style={{ color: "var(--warning)" }}>
              {cs.projectBrain.unmapped}
            </span>
          </div>
        ))}
      </div>
    </Panel>
  );
}

function LessonSection({ lessons }: { lessons: ProjectBrainDTO["lessons"] }) {
  return (
    <Panel className="p-3">
      <div className="af-label mb-2" style={{ color: "var(--positive)" }}>
        {cs.projectBrain.lessonTitle}
      </div>
      {lessons.length === 0 ? (
        <EmptyState title={cs.projectBrain.noLessons} note={cs.projectBrain.noLessonsNote} />
      ) : (
        <div className="flex flex-col gap-1.5">
          {lessons.map((l) => (
            <div key={l.slug} className="px-2 py-1.5" style={{ borderRadius: "var(--radius-box)", border: "1px solid var(--border-default)" }}>
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="text-[11px]" style={{ color: "var(--text-primary)" }}>
                  {l.title}
                </span>
                {l.occurrences > 1 ? (
                  <Chip tone="amber">{cs.brain.occurrences(l.occurrences)}</Chip>
                ) : null}
                {!l.indexed ? <Chip tone="amber">{cs.projectBrain.noTrigger}</Chip> : null}
              </div>
              <div className="mt-0.5 text-[11px]" style={{ color: "var(--text-tertiary)" }}>
                {l.indexed ? l.readWhen : cs.projectBrain.noTriggerNote}
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="mt-2 text-[11px]" style={{ color: "var(--text-tertiary)" }}>
        {cs.projectBrain.editNote}
      </p>
    </Panel>
  );
}
